import React, { type Ref } from 'react';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import FileIcon from '@atlaskit/icon/core/file';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';
import { ModalContextProvider } from '@atlassian/entry-points/modal-context-provider';
import { ModalTrigger } from '@atlassian/entry-points/modal-trigger';

import { loadingModalEntryPoint } from '../fixtures/constellation/loading-modal-entry-point';
import { RelayWrapper } from '../utils/wrapper';

const styles = cssMap({
	content: {
		paddingBlockStart: token('space.400'),
		paddingBlockEnd: token('space.400'),
		textAlign: 'center',
	},
	indicator: {
		paddingBlockStart: token('space.150'),
		paddingBlockEnd: token('space.150'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		borderRadius: token('radius.large'),
		backgroundColor: token('color.background.discovery.bold'),
	},
});

function CustomLoading({ onClose }: { onClose?: () => void }): React.JSX.Element {
	return (
		<>
			<ModalHeader hasCloseButton>
				<ModalTitle>Example Modal</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Stack space="space.300" alignInline="center" xcss={styles.content}>
					<Box aria-hidden="true" xcss={styles.indicator}>
						<Inline space="space.150" alignBlock="center">
							<FileIcon label="" color={token('color.icon.inverse')} />
							<Spinner size="medium" appearance="invert" label="" />
						</Inline>
					</Box>
					<Stack space="space.100" alignInline="center" role="status">
						<Text weight="bold">Preparing your modal</Text>
						<Text color="color.text.subtle">
							Loading your content. You can cancel while you wait.
						</Text>
					</Stack>
				</Stack>
			</ModalBody>
			<ModalFooter>
				<Button onClick={onClose}>Cancel</Button>
			</ModalFooter>
		</>
	);
}

export default function ModalTriggerCustomLoading(): React.JSX.Element {
	return (
		<RelayWrapper>
			<ModalContextProvider>
				<ModalTrigger entryPoint={loadingModalEntryPoint} Fallback={CustomLoading}>
					{({ ref }) => (
						<Button ref={ref as Ref<HTMLButtonElement>} aria-haspopup="dialog">
							Show custom loading
						</Button>
					)}
				</ModalTrigger>
			</ModalContextProvider>
		</RelayWrapper>
	);
}
